// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  DollarSign, 
  TrendingUp, 
  Target, 
  Users, 
  BarChart3, 
  AlertTriangle,
  Calendar,
  FileText,
  Download,
  Eye,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  Trophy,
  Activity,
  Phone,
  Mail,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight,
  Zap
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { parseLocalDate } from '@/utils/formatters';

interface SalesMetrics {
  totalRevenue: number;
  monthlyGrowth: number;
  pipelineValue: number;
  dealsClosedThisMonth: number;
  teamQuotaAttainment: number;
  avgDealSize: number;
}

interface SalesRepPerformance {
  rep_id: string;
  rep_name: string;
  revenue: number;
  deals_closed: number;
  pipeline_value: number;
  quota_attainment: number;
  calls_made: number;
  emails_sent: number;
  meetings_held: number;
  last_activity: string;
}

interface PipelineStage {
  stage: string;
  count: number;
  value: number;
  conversion_rate: number;
}

interface SegmentPerformance {
  segment: string;
  revenue: number;
  deal_count: number;
  growth_rate: number;
  conversion_rate: number;
}

interface DealAlert {
  id: string;
  prospect_name: string;
  value: number;
  stage: string;
  days_in_stage: number;
  rep_name: string;
  last_activity: string;
  alert_type: 'stale' | 'high_value' | 'overdue';
}

const AdminSalesOverview: React.FC = () => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<SalesMetrics>({
    totalRevenue: 0,
    monthlyGrowth: 0,
    pipelineValue: 0,
    dealsClosedThisMonth: 0,
    teamQuotaAttainment: 0,
    avgDealSize: 0
  });
  const [salesReps, setSalesReps] = useState<SalesRepPerformance[]>([]);
  const [pipelineStages, setPipelineStages] = useState<PipelineStage[]>([]);
  const [segmentPerformance, setSegmentPerformance] = useState<SegmentPerformance[]>([]);
  const [dealAlerts, setDealAlerts] = useState<DealAlert[]>([]);

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchSalesData();
    }
  }, [profile]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      
      // Fetch sales metrics
      await Promise.all([
        fetchSalesMetrics(),
        fetchSalesRepsPerformance(),
        fetchPipelineAnalytics(),
        fetchSegmentPerformance(),
        fetchDealAlerts()
      ]);
      
    } catch (error) {
      console.error('Error fetching sales data:', error);
      toast.error('Failed to load sales overview data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesMetrics = async () => {
    // Get advertising campaigns and partnerships revenue
    const [campaignsResponse, partnershipsResponse, prospectsResponse] = await Promise.all([
      supabase.from('advertising_campaigns').select('total_budget, campaign_status, start_date'),
      supabase.from('active_partnerships').select('monthly_revenue_generated, created_at'),
      supabase.from('advertiser_prospects').select('estimated_campaign_value, pipeline_stage')
    ]);

    // Calculate total revenue from closed deals
    const closedCampaigns = campaignsResponse.data?.filter(c => c.campaign_status === 'completed') || [];
    const campaignRevenue = closedCampaigns.reduce((sum, c) => sum + (c.total_budget || 0), 0);
    
    const partnershipRevenue = partnershipsResponse.data?.reduce((sum, p) => sum + (p.monthly_revenue_generated || 0), 0) || 0;
    const totalRevenue = campaignRevenue + (partnershipRevenue * 12); // Annualize monthly partnership revenue

    // Calculate pipeline value
    const pipelineValue = prospectsResponse.data
      ?.filter(p => p.pipeline_stage !== 'closed' && p.pipeline_stage !== 'lost')
      .reduce((sum, p) => sum + (p.estimated_campaign_value || 0), 0) || 0;

    // Calculate deals closed this month
    const thisMonth = new Date();
    thisMonth.setDate(1);
    const dealsThisMonth = closedCampaigns.filter(c => 
      c.start_date && parseLocalDate(c.start_date) >= thisMonth
    ).length;

    // Calculate average deal size
    const avgDealSize = closedCampaigns.length > 0 
      ? campaignRevenue / closedCampaigns.length 
      : 0;

    // Calculate monthly growth - compare this month vs last month
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    lastMonth.setDate(1);
    
    const lastMonthDeals = closedCampaigns.filter(c => {
      const dealDate = parseLocalDate(c.start_date);
      return dealDate >= lastMonth && dealDate < thisMonth;
    });
    
    const lastMonthRevenue = lastMonthDeals.reduce((sum, c) => sum + (c.total_budget || 0), 0);
    const currentMonthRevenue = closedCampaigns.filter(c => {
      const dealDate = parseLocalDate(c.start_date);
      return dealDate >= thisMonth;
    }).reduce((sum, c) => sum + (c.total_budget || 0), 0);
    
    const monthlyGrowth = lastMonthRevenue > 0 
      ? ((currentMonthRevenue - lastMonthRevenue) / lastMonthRevenue) * 100 
      : 0;

    setMetrics({
      totalRevenue,
      monthlyGrowth,
      pipelineValue,
      dealsClosedThisMonth: dealsThisMonth,
      teamQuotaAttainment: 0, // Will calculate after getting team data
      avgDealSize
    });
  };

  const fetchSalesRepsPerformance = async () => {
    // RLevents migration: role_assignments table dropped — sales rep listing is now empty.
    const salesRepsData: any[] | null = [];

    if (!salesRepsData) return;

    const repsWithPerformance = await Promise.all(
      salesRepsData.map(async (rep) => {
        // Get prospects assigned to this rep
        const { data: prospects } = await supabase
          .from('advertiser_prospects')
          .select('id, estimated_campaign_value, pipeline_stage, updated_at, created_at')
          .eq('sales_rep_id', rep.id);

    // Get campaigns for this rep's prospects  
        const prospectIds = prospects?.map(p => p.id) || [];
        const { data: campaigns } = prospectIds.length > 0 
          ? await supabase
              .from('advertising_campaigns')
              .select('total_budget, campaign_status')
              .in('advertiser_id', prospectIds)
          : { data: [] };

        const closedRevenue = campaigns
          ?.filter(c => c.campaign_status === 'completed')
          .reduce((sum, c) => sum + (c.total_budget || 0), 0) || 0;

        const dealsCount = campaigns?.filter(c => c.campaign_status === 'completed').length || 0;
        
        const pipelineValue = prospects
          ?.filter(p => p.pipeline_stage !== 'closed' && p.pipeline_stage !== 'lost')
          .reduce((sum, p) => sum + (p.estimated_campaign_value || 0), 0) || 0;

        return {
          rep_id: rep.id,
          rep_name: rep.name || rep.email,
          revenue: closedRevenue,
          deals_closed: dealsCount,
          pipeline_value: pipelineValue,
          quota_attainment: Math.min(100, (closedRevenue / 100000) * 100), // Assuming 100k quota
          calls_made: 0, // Would need to track in activities table
          emails_sent: 0, // Would need to track in activities table  
          meetings_held: 0, // Would need to track in activities table
          last_activity: prospects && prospects.length > 0 
            ? prospects.reduce((latest, p) => {
                const pDate = new Date(p.updated_at || p.created_at);
                const latestDate = new Date(latest);
                return pDate > latestDate ? pDate.toISOString() : latest;
              }, prospects[0].updated_at || prospects[0].created_at)
            : new Date(Date.now() - 86400000 * 7).toISOString()
        };
      })
    );

    setSalesReps(repsWithPerformance.sort((a, b) => b.revenue - a.revenue));
  };

  const fetchPipelineAnalytics = async () => {
    const { data: prospects } = await supabase
      .from('advertiser_prospects')
      .select('pipeline_stage, estimated_campaign_value');

    const stageGroups = prospects?.reduce((acc, prospect) => {
      const stage = prospect.pipeline_stage || 'prospect';
      if (!acc[stage]) {
        acc[stage] = { count: 0, value: 0 };
      }
      acc[stage].count++;
      acc[stage].value += prospect.estimated_campaign_value || 0;
      return acc;
    }, {} as Record<string, { count: number; value: number }>);

    const stages = Object.entries(stageGroups || {}).map(([stage, data]) => ({
      stage: stage.charAt(0).toUpperCase() + stage.slice(1),
      count: data.count,
      value: data.value,
      conversion_rate: 0 // Would need historical data to calculate actual conversion rates
    }));

    setPipelineStages(stages);
  };

  const fetchSegmentPerformance = async () => {
    // Get actual performance by prospect segment types
    const [advertiserData, affiliateData, partnerData] = await Promise.all([
      supabase.from('advertiser_prospects').select('estimated_campaign_value, pipeline_stage'),
      supabase.from('affiliate_prospects').select('*'),
      supabase.from('partner_prospects').select('*')
    ]);

    // Calculate actual revenue from closed advertiser deals
    const advertiserRevenue = advertiserData.data
      ?.filter(p => p.pipeline_stage === 'closed')
      .reduce((sum, p) => sum + (p.estimated_campaign_value || 0), 0) || 0;

    const segmentData = [
      {
        segment: 'Advertisers',
        revenue: advertiserRevenue,
        deal_count: advertiserData.data?.filter(p => p.pipeline_stage === 'closed').length || 0,
        growth_rate: 0, // Would need historical data to calculate growth
        conversion_rate: advertiserData.data && advertiserData.data.length > 0 
          ? (advertiserData.data.filter(p => p.pipeline_stage === 'closed').length / advertiserData.data.length) * 100
          : 0
      },
      {
        segment: 'Affiliates',
        revenue: 0, // Would need to track affiliate-generated revenue
        deal_count: affiliateData.data?.length || 0,
        growth_rate: 0, // Would need historical data
        conversion_rate: 0 // Would need conversion tracking
      },
      {
        segment: 'Partners',
        revenue: 0, // Would need to track partner revenue
        deal_count: partnerData.data?.length || 0,
        growth_rate: 0, // Would need historical data
        conversion_rate: 0 // Would need conversion tracking
      }
    ];

    setSegmentPerformance(segmentData);
  };

  const fetchDealAlerts = async () => {
    // Get deals that need attention
    const { data: prospects } = await supabase
      .from('advertiser_prospects')
      .select(`
        id,
        company_name,
        estimated_campaign_value,
        pipeline_stage,
        updated_at,
        sales_rep_id,
        profiles!advertiser_prospects_sales_rep_id_fkey(name)
      `)
      .not('pipeline_stage', 'in', '("closed","lost")');

    const alerts: DealAlert[] = prospects?.map(prospect => {
      const daysSinceUpdate = Math.floor(
        (Date.now() - new Date(prospect.updated_at).getTime()) / (1000 * 60 * 60 * 24)
      );

      let alertType: 'stale' | 'high_value' | 'overdue' = 'stale';
      if ((prospect.estimated_campaign_value || 0) > 50000) alertType = 'high_value';
      if (daysSinceUpdate > 14) alertType = 'overdue';

      return {
        id: prospect.id,
        prospect_name: prospect.company_name,
        value: prospect.estimated_campaign_value || 0,
        stage: prospect.pipeline_stage,
        days_in_stage: daysSinceUpdate,
        rep_name: (prospect.profiles as any)?.name || 'Unassigned',
        last_activity: prospect.updated_at,
        alert_type: alertType
      };
    }).filter(alert => alert.alert_type === 'overdue' || alert.alert_type === 'high_value')
      .sort((a, b) => b.value - a.value)
      .slice(0, 10) || [];

    setDealAlerts(alerts);
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'high_value': return 'bg-emerald-100 text-emerald-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2">Loading sales overview...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Sales Overview</h1>
          <p className="text-muted-foreground mt-2">
            Comprehensive sales analytics and team performance management
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button variant="outline" size="sm">
            <Calendar className="h-4 w-4 mr-2" />
            Schedule Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(metrics.totalRevenue)}</p>
                <div className="flex items-center mt-1">
                  <ArrowUpRight className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600 font-medium">+{metrics.monthlyGrowth}%</span>
                </div>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pipeline Value</p>
                <p className="text-2xl font-bold">{formatCurrency(metrics.pipelineValue)}</p>
                <p className="text-sm text-muted-foreground mt-1">Weighted forecast</p>
              </div>
              <Target className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Team Quota</p>
                <p className="text-2xl font-bold">{metrics.teamQuotaAttainment}%</p>
                <Progress value={metrics.teamQuotaAttainment} className="mt-2" />
              </div>
              <Trophy className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Deals Closed</p>
                <p className="text-2xl font-bold">{metrics.dealsClosedThisMonth}</p>
                <p className="text-sm text-muted-foreground mt-1">This month</p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Deal Size</p>
                <p className="text-2xl font-bold">{formatCurrency(metrics.avgDealSize)}</p>
                <p className="text-sm text-muted-foreground mt-1">Per deal</p>
              </div>
              <BarChart3 className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Reps</p>
                <p className="text-2xl font-bold">{salesReps.length}</p>
                <p className="text-sm text-muted-foreground mt-1">Team members</p>
              </div>
              <Users className="h-8 w-8 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="team" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="team">Team Performance</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline Analytics</TabsTrigger>
          <TabsTrigger value="segments">Segment Performance</TabsTrigger>
          <TabsTrigger value="alerts">Alerts & Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Sales Team Performance</CardTitle>
              <CardDescription>Individual rep performance and activity metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Rep Name</th>
                      <th className="text-left py-3 px-4 font-medium">Revenue</th>
                      <th className="text-left py-3 px-4 font-medium">Deals</th>
                      <th className="text-left py-3 px-4 font-medium">Pipeline</th>
                      <th className="text-left py-3 px-4 font-medium">Quota</th>
                      <th className="text-left py-3 px-4 font-medium">Activities</th>
                      <th className="text-left py-3 px-4 font-medium">Last Activity</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesReps.map((rep, index) => (
                      <tr key={rep.rep_id} className="border-b hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium">{index + 1}</span>
                            </div>
                            <div>
                              <p className="font-medium">{rep.rep_name}</p>
                              {index < 3 && (
                                <Badge variant="secondary" className="text-xs">
                                  Top Performer
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="font-semibold">{formatCurrency(rep.revenue)}</p>
                        </td>
                        <td className="py-4 px-4">
                          <p className="font-medium">{rep.deals_closed}</p>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-sm">{formatCurrency(rep.pipeline_value)}</p>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <Progress value={rep.quota_attainment} className="w-16" />
                            <span className="text-sm font-medium">{rep.quota_attainment.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {rep.calls_made}
                            </div>
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {rep.emails_sent}
                            </div>
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {rep.meetings_held}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <p className="text-sm text-muted-foreground">
                            {new Date(rep.last_activity).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="py-4 px-4">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pipeline" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Pipeline by Stage</CardTitle>
                <CardDescription>Deal distribution and conversion rates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pipelineStages.map((stage) => (
                    <div key={stage.stage} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{stage.stage}</h4>
                        <p className="text-sm text-muted-foreground">
                          {stage.count} deals • {formatCurrency(stage.value)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{stage.conversion_rate.toFixed(1)}%</p>
                        <p className="text-sm text-muted-foreground">Conversion</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Pipeline Health</CardTitle>
                <CardDescription>Key performance indicators</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Average Deal Velocity</span>
                      <span className="text-sm">32 days</span>
                    </div>
                    <Progress value={68} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Win Rate</span>
                      <span className="text-sm">23.5%</span>
                    </div>
                    <Progress value={23.5} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Forecast Accuracy</span>
                      <span className="text-sm">87%</span>
                    </div>
                    <Progress value={87} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium">Pipeline Coverage</span>
                      <span className="text-sm">3.2x</span>
                    </div>
                    <Progress value={80} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="segments" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Segment Performance Breakdown</CardTitle>
              <CardDescription>Revenue and growth by prospect segment</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {segmentPerformance.map((segment) => (
                  <div key={segment.segment} className="p-6 border rounded-lg">
                    <h3 className="font-semibold text-lg mb-4">{segment.segment}</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Revenue</span>
                        <span className="font-semibold">{formatCurrency(segment.revenue)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Deals</span>
                        <span className="font-semibold">{segment.deal_count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Growth</span>
                        <div className="flex items-center gap-1">
                          {segment.growth_rate > 0 ? (
                            <ArrowUpRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-600" />
                          )}
                          <span className={`font-semibold ${segment.growth_rate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {segment.growth_rate > 0 ? '+' : ''}{segment.growth_rate.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Conversion</span>
                        <span className="font-semibold">{segment.conversion_rate.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Deals Requiring Attention
                </CardTitle>
                <CardDescription>High-value and overdue opportunities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dealAlerts.map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{alert.prospect_name}</h4>
                          <Badge className={getAlertColor(alert.alert_type)}>
                            {alert.alert_type === 'high_value' ? 'High Value' : 'Overdue'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {formatCurrency(alert.value)} • {alert.stage} • {alert.rep_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {alert.days_in_stage} days in stage
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-600" />
                  Team Activity Alerts
                </CardTitle>
                <CardDescription>Performance and activity notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Zap className="h-5 w-5 text-yellow-600" />
                    <div className="flex-1">
                      <p className="font-medium">Low Activity Alert</p>
                      <p className="text-sm text-muted-foreground">
                        2 reps below minimum activity targets this week
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">View</Button>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Trophy className="h-5 w-5 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium">Quota Achievement</p>
                      <p className="text-sm text-muted-foreground">
                        3 reps exceeded monthly quota
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">View</Button>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600" />
                    <div className="flex-1">
                      <p className="font-medium">Follow-up Overdue</p>
                      <p className="text-sm text-muted-foreground">
                        15 prospects need follow-up actions
                      </p>
                    </div>
                    <Button variant="ghost" size="sm">View</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSalesOverview;